import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  Clock, 
  Tag,
  AlertCircle,
  RefreshCw,
  Pencil,
  ChevronsUpDown,
  Check,
  FolderOpen,
  CalendarDays,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import { fetchTaskContent, updateTask } from "@/lib/notion-client"
import { getTagColors } from '@/lib/tag-colors';
import { getStatusColors } from '@/lib/status-colors';
import { useProductionManagement } from '@/lib/production-management-context';
import { useSprints } from '@/lib/sprint-context';
import { CommentsSection } from '@/components/comments-section';
import { NotionAvatar } from '@/components/notion-avatar';
import MarkdownContent from '@/components/markdown-content';
import { NotionIcon } from '@/components/icons/notion-icon';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type { SprintTask } from '@/lib/types';

export default function ProductionManagementTaskPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { resources, projects, users, getUsersByEmails } = useProductionManagement();
  const { sprintTasks, sprints, loadingSprints, refreshSprints } = useSprints();
  
  // Get task data from navigation state if available
  const taskFromState = location.state?.task as SprintTask | undefined;
  
  // Try to find task in sprintTasks context if not in state
  const taskFromContext = useMemo(() => {
    if (taskFromState) return null; // Already have from state
    if (!taskId || !sprintTasks.length) return null;
    return sprintTasks.find(t => t.id === taskId) || null;
  }, [taskId, sprintTasks, taskFromState]);
  
  const initialTask = taskFromState || taskFromContext;
  
  const [task, setTask] = useState<SprintTask | null>(initialTask);
  const [taskContent, setTaskContent] = useState<string>('');
  const [loading, setLoading] = useState(!initialTask);
  const [error, setError] = useState<string | null>(null);
  
  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editHoursCREA, setEditHoursCREA] = useState('');
  const [editHoursCONSLT, setEditHoursCONSLT] = useState('');
  const [editHoursMKT, setEditHoursMKT] = useState('');
  const [editHoursTECH, setEditHoursTECH] = useState('');
  const [editHoursBIZ, setEditHoursBIZ] = useState('');
  const [editHoursKAIZEN, setEditHoursKAIZEN] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editProjectId, setEditProjectId] = useState('');
  const [editContributors, setEditContributors] = useState<string[]>([]);
  const [editDueDate, setEditDueDate] = useState('');
  const [editSprintId, setEditSprintId] = useState('');
  const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [contributorSearchTerm, setContributorSearchTerm] = useState('');
  const [isSprintPickerOpen, setIsSprintPickerOpen] = useState(false);
  const [sprintSearchTerm, setSprintSearchTerm] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Get project name from project ID
  const getProjectName = (projectId?: string): string => {
    if (!projectId) return 'Not assigned';
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };
  
  // Get sprint name from sprint ID
  const getSprintName = (sprintId?: string): string => {
    if (!sprintId) return 'Not assigned';
    const sprint = sprints.find(s => s.id === sprintId);
    return sprint?.name || 'Unknown Sprint';
  };
  
  // Filtered projects for picker
  const filteredProjects = useMemo(() => {
    if (!projectSearchTerm) return projects;
    return projects.filter(p => 
      p.name?.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      (p as any).projectCode?.toLowerCase().includes(projectSearchTerm.toLowerCase())
    );
  }, [projects, projectSearchTerm]);
  
  // Filtered resources (contributors) for picker
  const filteredResources = useMemo(() => {
    const activeResources = resources.filter(r => r.active !== false);
    if (!contributorSearchTerm) return activeResources;
    return activeResources.filter(r => 
      r.name?.toLowerCase().includes(contributorSearchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(contributorSearchTerm.toLowerCase())
    );
  }, [resources, contributorSearchTerm]);
  
  // Filtered sprints for picker (sorted by date desc)
  const filteredSprints = useMemo(() => {
    const sorted = [...sprints].sort((a, b) => {
      const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
      return bDate - aDate;
    });
    if (!sprintSearchTerm) return sorted;
    return sorted.filter(s => 
      s.name?.toLowerCase().includes(sprintSearchTerm.toLowerCase())
    );
  }, [sprints, sprintSearchTerm]);
  
  // Available tags (collect from all tasks)
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    sprintTasks.forEach(t => {
      t.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [sprintTasks]);
  
  // Update task when context loads (for direct URL access)
  useEffect(() => {
    if (!task && taskFromContext) {
      setTask(taskFromContext);
      setLoading(false);
      setError(null); // Clear any previous error
    }
  }, [taskFromContext, task]);

  // Helper function to get contributor names from IDs
  const getContributorNames = (contributorIds?: string[]): string => {
    if (!contributorIds || contributorIds.length === 0) return 'None'
    
    const names = contributorIds
      .map(id => {
        const resource = resources.find(r => r.id === id)
        return resource?.name?.trim() || null
      })
      .filter(Boolean) as string[]
    
    // If we have matched names, show them. Otherwise show count
    if (names.length > 0) {
      return names.join(', ')
    }
    
    // Show count of contributors instead of IDs
    return `${contributorIds.length} contributor${contributorIds.length !== 1 ? 's' : ''}`
  }

  const fetchTask = async (force: boolean = false) => {
    if (!taskId) return;
    
    try {
      setError(null);
      
      const wp = (window as any).WP_COCKPIT;
      const isDev = wp?.devMode || false;
      
      if (isDev) {
        console.log('[ProductionTaskPage] Fetching task content for:', taskId);
        console.log('[ProductionTaskPage] Force refresh:', force);
        console.log('[ProductionTaskPage] Task from state:', taskFromState);
        console.log('[ProductionTaskPage] Task from context:', taskFromContext);
        console.log('[ProductionTaskPage] Initial task:', initialTask);
      }
      
      // If we have task data from state or context, use it
      const taskSource = taskFromState || taskFromContext;
      if (taskSource && !task) {
        if (isDev) {
          console.log('[ProductionTaskPage] Setting task from source:', taskSource)
          console.log('[ProductionTaskPage] Task assignee field:', taskSource.assignee)
          console.log('[ProductionTaskPage] Task assignee type:', typeof taskSource.assignee)
          console.log('[ProductionTaskPage] Task assignee is array:', Array.isArray(taskSource.assignee))
        }
        setTask(taskSource);
      }
      
      const data = await fetchTaskContent(taskId, force);
      
      if (isDev) console.log('[ProductionTaskPage] Response data:', data);
      
      if (!data) {
        // Only show error if we also don't have task from context
        if (!taskFromState && !taskFromContext) {
          setError('Task content not found');
        }
        return;
      }
      
      if (data.content) {
        setTaskContent(data.content);
      } else {
        setTaskContent('');
      }
      
      // If we don't have task metadata from state or context, show a message
      // But wait for sprints to load first
      if (!taskFromState && !taskFromContext && !loadingSprints) {
        setError('Task not found. It may have been deleted or you may not have access.');
      }
    } catch (err) {
      console.error('Error fetching task:', err);
      if (!taskFromState && !taskFromContext) {
        setError('Failed to load task content');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
  }, [taskId]);
  
  // Re-run fetchTask when context loads (for direct URL access)
  useEffect(() => {
    if (taskFromContext && !taskContent) {
      fetchTask();
    }
  }, [taskFromContext]);

  // Handle saving task edits
  const handleSaveEdit = async () => {
    if (!task || !taskId) return;
    
    setIsSavingEdit(true);
    try {
      // Calculate total estimated hours from department hours
      const hoursCREA = editHoursCREA ? parseFloat(editHoursCREA) : 0;
      const hoursCONSLT = editHoursCONSLT ? parseFloat(editHoursCONSLT) : 0;
      const hoursMKT = editHoursMKT ? parseFloat(editHoursMKT) : 0;
      const hoursTECH = editHoursTECH ? parseFloat(editHoursTECH) : 0;
      const hoursBIZ = editHoursBIZ ? parseFloat(editHoursBIZ) : 0;
      const hoursKAIZEN = editHoursKAIZEN ? parseFloat(editHoursKAIZEN) : 0;
      const totalHours = hoursCREA + hoursCONSLT + hoursMKT + hoursTECH + hoursBIZ + hoursKAIZEN;
      
      await updateTask({
        taskId,
        projectId: editProjectId || undefined,
        contributors: editContributors.length > 0 ? editContributors : undefined,
        tags: editTags.length > 0 ? editTags : undefined,
        estimatedHours: totalHours > 0 ? totalHours : undefined,
        estimatedHoursCREA: hoursCREA > 0 ? hoursCREA : undefined,
        estimatedHoursCONSLT: hoursCONSLT > 0 ? hoursCONSLT : undefined,
        estimatedHoursMKT: hoursMKT > 0 ? hoursMKT : undefined,
        estimatedHoursTECH: hoursTECH > 0 ? hoursTECH : undefined,
        estimatedHoursBIZ: hoursBIZ > 0 ? hoursBIZ : undefined,
        estimatedHoursKAIZEN: hoursKAIZEN > 0 ? hoursKAIZEN : undefined,
        dueDate: editDueDate || undefined,
        sprintId: editSprintId || undefined,
      });
      
      // Update local task state
      setTask(prev => prev ? {
        ...prev,
        projectId: editProjectId || prev.projectId,
        contributors: editContributors.length > 0 ? editContributors : prev.contributors,
        tags: editTags.length > 0 ? editTags : prev.tags,
        estimatedHours: totalHours > 0 ? totalHours : prev.estimatedHours,
        due: editDueDate ? new Date(editDueDate) : prev.due,
        sprints: editSprintId ? [editSprintId] : prev.sprints,
      } : null);
      
      // Refresh sprints context to get updated data
      refreshSprints();
      
      toast({
        title: 'Task updated',
        description: 'The task has been updated successfully.',
      });
      
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update task:', err);
      toast({
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'Failed to update task',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const formatDate = (date?: Date | null) => {
    if (!date) return 'N/A';
    try {
      return format(date, 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  // Show loading if we're still loading sprints and don't have task yet
  if (loading || (loadingSprints && !task)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6"></div>
            <div className="grid gap-6 md:grid-cols-2">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Task not found'}</AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate('/production-management')} 
            variant="outline" 
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Production Management
          </Button>
        </div>
      </div>
    );
  }

  const wp = (window as any).WP_COCKPIT;
  const isDev = wp?.devMode || false;
  
  if (isDev) {
    console.log('[ProductionTaskPage RENDER] Current task object:', task)
    console.log('[ProductionTaskPage RENDER] task.assignee:', task.assignee)
    console.log('[ProductionTaskPage RENDER] task.assignee exists?', !!task.assignee)
    console.log('[ProductionTaskPage RENDER] task.assignee length:', task.assignee?.length)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            onClick={() => navigate(-1)} 
            variant="outline" 
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {task.title}
                </h1>
                <Badge className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                  {task.status}
                </Badge>
              </div>
              {task.description && task.description !== "Loading..." && (
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {task.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => window.open(`https://www.notion.so/${task.id.replace(/-/g, '')}`, '_blank')}
                    variant="outline"
                    size="icon"
                  >
                    <NotionIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View in Notion</p>
                </TooltipContent>
              </Tooltip>
              <Button 
                onClick={() => {
                  // Initialize edit form with current values
                  setEditProjectId(task.projectId || '');
                  setEditContributors(task.contributors || []);
                  setEditTags(task.tags || []);
                  // Initialize department hour fields (task may have these properties from backend)
                  const taskAny = task as any;
                  setEditHoursCREA(taskAny.estimatedHoursCREA?.toString() || '');
                  setEditHoursCONSLT(taskAny.estimatedHoursCONSLT?.toString() || '');
                  setEditHoursMKT(taskAny.estimatedHoursMKT?.toString() || '');
                  setEditHoursTECH(taskAny.estimatedHoursTECH?.toString() || '');
                  setEditHoursBIZ(taskAny.estimatedHoursBIZ?.toString() || '');
                  setEditHoursKAIZEN(taskAny.estimatedHoursKAIZEN?.toString() || '');
                  setEditDueDate(task.due ? format(new Date(task.due), 'yyyy-MM-dd') : '');
                  setEditSprintId(task.sprints?.[0] || '');
                  setContributorSearchTerm('');
                  setSprintSearchTerm('');
                  setIsEditDialogOpen(true);
                }}
                variant="outline"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button onClick={() => fetchTask(true)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task Details */}
            <Card>
              <CardHeader>
                <CardTitle>Task Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {task.due && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Due Date:</span>
                      <span className="text-sm font-medium">{formatDate(task.due)}</span>
                    </div>
                  )}
                  
                  {task.assignee && task.assignee.length > 0 && (() => {
                    const assigneeUsers = getUsersByEmails(task.assignee)
                    return (
                      <div className="flex items-center gap-2 md:col-span-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Assigned to:</span>
                        <div className="flex items-center gap-2">
                          {assigneeUsers.map(user => (
                            <div key={user.id} className="flex items-center gap-1.5">
                              <NotionAvatar 
                                name={user.name}
                                profileImage={user.profileImage}
                                className="h-6 w-6"
                              />
                              <span className="text-sm font-medium">{user.name}</span>
                            </div>
                          ))}
                          {task.assignee.length > assigneeUsers.length && (
                            <span className="text-sm text-muted-foreground">
                              +{task.assignee.length - assigneeUsers.length} more
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                  
                  {task.contributors && task.contributors.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Contributors:</span>
                      <span className="text-sm">{getContributorNames(task.contributors)}</span>
                    </div>
                  )}
                  
                  {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Hours:</span>
                      <span className="text-sm">
                        {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                          <>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</>
                        ) : task.realisedHours !== undefined ? (
                          <>{task.realisedHours.toFixed(1)}h tracked</>
                        ) : (
                          <>{task.estimatedHours}h estimated</>
                        )}
                      </span>
                    </div>
                  )}

                  {task.priority && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Priority:</span>
                      <span className="text-sm font-medium">{task.priority}</span>
                    </div>
                  )}
                  
                  {task.tags && task.tags.length > 0 && (
                    <div className="flex items-center gap-2 md:col-span-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Tags:</span>
                      <div className="flex flex-wrap gap-1">
                        {task.tags.map((tag, idx) => {
                          const colors = getTagColors(tag);
                          return (
                            <Badge 
                              key={idx} 
                              className={`${colors.bg} ${colors.text} ${colors.border} border text-xs`}
                            >
                              {tag}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {task.projectId && (
                    <div className="flex items-center gap-2 md:col-span-2">
                      <FolderOpen className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Project:</span>
                      <Link 
                        to={`/production-management/projects/${task.projectId}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 hover:bg-primary/20 text-primary rounded-md text-sm font-medium transition-colors"
                      >
                        <FolderOpen className="h-3.5 w-3.5" />
                        {getProjectName(task.projectId)}
                      </Link>
                    </div>
                  )}
                  
                  {task.sprints && task.sprints.length > 0 && task.sprints[0] && (
                    <div className="flex items-center gap-2 md:col-span-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Sprint:</span>
                      <Badge variant="outline" className="text-sm font-medium">
                        {getSprintName(task.sprints[0])}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Task Content */}
            {taskContent && (
              <Card>
                <CardHeader>
                  <CardTitle>Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <MarkdownContent content={taskContent} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Comments */}
            {taskId && (
              <CommentsSection 
                id={taskId}
                type="task"
              />
            )}
          </div>
        </div>
      </div>

      {/* Edit Task Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update the task properties below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Project Picker */}
            <div className="space-y-2">
              <Label>Project</Label>
              <Popover open={isProjectPickerOpen} onOpenChange={setIsProjectPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isProjectPickerOpen}
                    className="w-full justify-between"
                  >
                    {editProjectId ? getProjectName(editProjectId) : 'Select project...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search projects..." 
                      value={projectSearchTerm}
                      onChange={(e) => setProjectSearchTerm(e.target.value)}
                    />
                    <CommandList>
                      <CommandEmpty>No project found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setEditProjectId('');
                            setIsProjectPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              editProjectId === '' ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Not assigned
                        </CommandItem>
                        {filteredProjects.map((project) => (
                          <CommandItem
                            key={project.id}
                            onSelect={() => {
                              setEditProjectId(project.id);
                              setIsProjectPickerOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                editProjectId === project.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {project.name || 'Unnamed Project'}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Assignees - Notion Users */}
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <div className="flex flex-wrap gap-2 min-h-[36px] p-2 border rounded-md bg-muted/50">
                {(task?.assignee || []).map((email) => {
                  const user = users.find(u => u.email === email);
                  return (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {user && user.profileImage && (
                        <img src={user.profileImage} className="h-4 w-4 rounded-full" alt="" />
                      )}
                      {user?.name || email}
                    </Badge>
                  );
                })}
                {(!task?.assignee || task.assignee.length === 0) && (
                  <span className="text-sm text-muted-foreground">No assignees</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Assignees cannot be changed from here</p>
            </div>
            
            {/* Contributors - Resources (Searchable) */}
            <div className="space-y-2">
              <Label>Contributors</Label>
              <div className="flex flex-wrap gap-2 min-h-[36px] p-2 border rounded-md bg-background">
                {editContributors.map((resourceId) => {
                  const resource = resources.find(r => r.id === resourceId);
                  return (
                    <Badge
                      key={resourceId}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setEditContributors(editContributors.filter(id => id !== resourceId))}
                    >
                      {resource?.name || resourceId} ×
                    </Badge>
                  );
                })}
                {editContributors.length === 0 && (
                  <span className="text-sm text-muted-foreground">No contributors</span>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search contributors..."
                  value={contributorSearchTerm}
                  onChange={(e) => setContributorSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              {contributorSearchTerm && (
                <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto border rounded-md p-2">
                  {filteredResources.filter(r => !editContributors.includes(r.id)).length === 0 ? (
                    <span className="text-sm text-muted-foreground">No matching contributors</span>
                  ) : (
                    filteredResources.filter(r => !editContributors.includes(r.id)).map(resource => (
                      <Badge
                        key={resource.id}
                        variant="outline"
                        className="cursor-pointer text-xs hover:bg-accent"
                        onClick={() => {
                          setEditContributors([...editContributors, resource.id]);
                          setContributorSearchTerm('');
                        }}
                      >
                        + {resource.name}
                      </Badge>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 min-h-[36px] p-2 border rounded-md bg-background">
                {editTags.map((tag, idx) => {
                  const colors = getTagColors(tag);
                  return (
                    <Badge
                      key={idx}
                      className={`${colors.bg} ${colors.text} ${colors.border} border cursor-pointer hover:opacity-70`}
                      onClick={() => setEditTags(editTags.filter((_, i) => i !== idx))}
                    >
                      {tag} ×
                    </Badge>
                  );
                })}
                {editTags.length === 0 && (
                  <span className="text-sm text-muted-foreground">No tags selected</span>
                )}
              </div>
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {availableTags.filter(t => !editTags.includes(t)).map(tag => {
                    const colors = getTagColors(tag);
                    return (
                      <Badge
                        key={tag}
                        variant="outline"
                        className={`cursor-pointer text-xs hover:${colors.bg}`}
                        onClick={() => setEditTags([...editTags, tag])}
                      >
                        + {tag}
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Estimated Hours by Department */}
            <div className="space-y-2">
              <Label>Estimated Hours by Department</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">CREA (Creative)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursCREA}
                    onChange={(e) => setEditHoursCREA(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">CONSLT (Consulting)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursCONSLT}
                    onChange={(e) => setEditHoursCONSLT(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">MKT (Marketing)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursMKT}
                    onChange={(e) => setEditHoursMKT(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">TECH</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursTECH}
                    onChange={(e) => setEditHoursTECH(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">BIZ (Business)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursBIZ}
                    onChange={(e) => setEditHoursBIZ(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">KAIZEN</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHoursKAIZEN}
                    onChange={(e) => setEditHoursKAIZEN(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total: {(
                  (parseFloat(editHoursCREA) || 0) +
                  (parseFloat(editHoursCONSLT) || 0) +
                  (parseFloat(editHoursMKT) || 0) +
                  (parseFloat(editHoursTECH) || 0) +
                  (parseFloat(editHoursBIZ) || 0) +
                  (parseFloat(editHoursKAIZEN) || 0)
                ).toFixed(1)}h
              </p>
            </div>
            
            {/* Due Date */}
            <div className="space-y-2">
              <Label>Due Date</Label>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
                {editDueDate && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEditDueDate('')}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Sprint */}
            <div className="space-y-2">
              <Label>Sprint</Label>
              <Popover open={isSprintPickerOpen} onOpenChange={setIsSprintPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isSprintPickerOpen}
                    className="w-full justify-between"
                  >
                    {editSprintId ? getSprintName(editSprintId) : 'Select sprint...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search sprints..." 
                      value={sprintSearchTerm}
                      onChange={(e) => setSprintSearchTerm(e.target.value)}
                    />
                    <CommandList>
                      <CommandEmpty>No sprint found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setEditSprintId('');
                            setIsSprintPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              editSprintId === '' ? "opacity-100" : "opacity-0"
                            )}
                          />
                          No sprint
                        </CommandItem>
                        {filteredSprints.map((sprint) => (
                          <CommandItem
                            key={sprint.id}
                            onSelect={() => {
                              setEditSprintId(sprint.id);
                              setIsSprintPickerOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                editSprintId === sprint.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {sprint.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
